import { FC, useEffect, useState } from 'react';
import { useToast, UseToastOptions } from '@chakra-ui/react';
import { removeAlertAction } from 'app/store/Alerts/slice';
import { Alert as AlertType } from 'app/store/Alerts/slice.types';
import { AppDispatch, useAppDispatch, useAppSelector } from 'app/store';
import { SNACKBAR_DEFAULT_CLOSE_TIME } from 'app/components/Alerts/constants';
import { getAlerts } from 'app/store/Alerts/selectors';
import { getAudioPlayer } from 'app/store/AudioPlayer/selectors';
import { Alert } from '../Core/ChakraAlert';

interface AlertsComponentProps {
  dispatch: AppDispatch;
  alerts: AlertType[];
}

const getCommonAlertProps = (alertData: AlertType): UseToastOptions => ({
  render: ({ onClose }) => (
    <Alert
      onClick={onClose}
      title={alertData.message}
      icon={alertData.statusIcon}
    />
  ),
  position: 'bottom',
  containerStyle: {
    minWidth: 240,
    marginTop: 2,
  },
  duration:
    alertData.autoHideDuration === null
      ? null
      : alertData.autoHideDuration || SNACKBAR_DEFAULT_CLOSE_TIME,
});

export const AlertsComponent: FC<AlertsComponentProps> = ({
  dispatch,
  alerts,
}) => {
  const toast = useToast();
  const [persistedAlerts, setPersistedAlerts] = useState({});
  const priorityAlert = alerts.length > 0 ? alerts[0] : null;

  useEffect(() => {
    if (!priorityAlert) {
      return;
    }

    dispatch(removeAlertAction(0));

    if (!priorityAlert.id) {
      toast(getCommonAlertProps(priorityAlert));
      return;
    }

    // handle alert update
    const persistedAlertProps = {
      ...getCommonAlertProps(priorityAlert),
      onCloseComplete: () => {
        setPersistedAlerts({
          ...persistedAlerts,
          [priorityAlert.id as string]: undefined,
        });
      },
    };
    const doesAlertExist = persistedAlerts[priorityAlert.id];

    if (doesAlertExist) {
      const refToUpdate = persistedAlerts[priorityAlert.id];
      toast.update(refToUpdate, persistedAlertProps);
      return;
    }

    const createdToastRef = toast(persistedAlertProps);

    setPersistedAlerts({
      ...persistedAlerts,
      [priorityAlert.id]: createdToastRef,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [priorityAlert]);

  return null;
};

export const Alerts = () => {
  const dispatch = useAppDispatch();
  const alerts = useAppSelector(getAlerts);
  const { isOpen } = useAppSelector(getAudioPlayer);

  useEffect(() => {
    const toastPositionManager = document.getElementById(
      'chakra-toast-manager-bottom-left'
    );
    if (toastPositionManager) {
      if (isOpen) {
        toastPositionManager.classList.add('lifted');
      } else {
        toastPositionManager.classList.remove('lifted');
      }
    }
  }, [isOpen]);

  return <AlertsComponent dispatch={dispatch} alerts={alerts} />;
};
